"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { X, Menu } from "lucide-react";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Features", href: "/#features" },
  { label: "Pricing", href: "/#pricing" },
  { label: "FAQ", href: "/#faq" },
  { label: "Guide", href: "/guide" },
  { label: "Reseller", href: "/reseller" },
  { label: "Affiliates", href: "/affiliates" },
  { label: "About", href: "/about" },
];

export default function Navbar() {
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [searchParams]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      <nav className="bg-card border-b-2 border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              href="/"
              onClick={(e) => {
                if (window.location.pathname === "/" && window.location.hash) {
                  e.preventDefault();
                  history.replaceState(null, "", "/");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              className="shrink-0"
            >
              <Image
                src="/icon0.svg"
                alt="Logo"
                height={110}
                width={110}
                priority
              />
            </Link>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="relative font-heading text-xs font-700 uppercase tracking-widest text-muted-foreground hover:text-foreground px-3 py-1.5 transition-colors duration-150 group"
                >
                  {link.label}
                  <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:block">
              <Link
                href="/contact"
                className="bg-primary text-primary-foreground font-heading font-800 text-xs uppercase tracking-widest px-6 py-3 hover:bg-foreground hover:text-background transition-colors duration-200"
              >
                Contact
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 text-foreground"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-50 bg-background flex flex-col transition-opacity duration-300 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between h-16 px-4 border-b-2 border-primary shrink-0">
          <Link href="/" onClick={() => setIsOpen(false)}>
            <Image src="/icon0.svg" alt="Logo" height={110} width={110} />
          </Link>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 text-foreground"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Links Container */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <nav className="flex flex-col px-6 sm:px-8 pt-4 sm:pt-6 pb-4 gap-1 stagger">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="font-heading text-2xl sm:text-3xl md:text-4xl font-800 uppercase tracking-tight text-foreground border-b border-border py-3 sm:py-4 hover:text-primary transition-colors duration-150"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Bottom CTA */}
        <div className="shrink-0 px-6 sm:px-8 pb-6 sm:pb-8 pt-4 border-t border-border">
          <Link
            href="/contact"
            onClick={() => setIsOpen(false)}
            className="block bg-primary text-primary-foreground font-heading font-800 text-sm uppercase tracking-widest px-6 sm:px-8 py-3 sm:py-4 text-center hover:bg-foreground hover:text-background transition-colors duration-200"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </>
  );
}
